import { useEffect, useRef, useState } from 'react';

import {
  disposeMotionPreviewGltfAsset,
  loadMotionPreviewGltfAsset,
  type CartographerMotionPreviewGltfAsset,
} from './motionPreviewAsset';

interface UseMotionPreviewGltfAssetOptions {
  enabled: boolean;
  gltfUrl: string | null;
}

interface UseMotionPreviewGltfAssetResult {
  asset: CartographerMotionPreviewGltfAsset | null;
  error: Error | null;
  isLoading: boolean;
}

interface LoadedMotionPreviewGltfAssetState {
  asset: CartographerMotionPreviewGltfAsset;
  gltfUrl: string;
}

export default function useMotionPreviewGltfAsset({
  enabled,
  gltfUrl,
}: UseMotionPreviewGltfAssetOptions): UseMotionPreviewGltfAssetResult {
  const [loadedAssetState, setLoadedAssetState] =
    useState<LoadedMotionPreviewGltfAssetState | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const loadedAssetStateRef = useRef<LoadedMotionPreviewGltfAssetState | null>(
    null,
  );
  const requestSeqRef = useRef(0);

  const asset =
    enabled &&
    gltfUrl &&
    loadedAssetState?.gltfUrl === gltfUrl
      ? loadedAssetState.asset
      : null;

  useEffect(() => {
    const requestSeq = ++requestSeqRef.current;
    const currentLoadedAssetState = loadedAssetStateRef.current;
    const shouldDisposeCurrentAsset =
      currentLoadedAssetState != null &&
      (!enabled || !gltfUrl || currentLoadedAssetState.gltfUrl !== gltfUrl);

    if (shouldDisposeCurrentAsset) {
      loadedAssetStateRef.current = null;
      setLoadedAssetState(null);
      disposeMotionPreviewGltfAsset(currentLoadedAssetState.asset);
    }

    if (!enabled || !gltfUrl) {
      setError(null);
      setIsLoading(false);
      return;
    }

    setError(null);
    setIsLoading(true);

    void loadMotionPreviewGltfAsset(gltfUrl)
      .then((resolvedAsset) => {
        if (requestSeq !== requestSeqRef.current) {
          disposeMotionPreviewGltfAsset(resolvedAsset);
          return;
        }

        const nextLoadedAssetState = resolvedAsset
          ? {
              asset: resolvedAsset,
              gltfUrl,
            }
          : null;
        loadedAssetStateRef.current = nextLoadedAssetState;
        setLoadedAssetState(nextLoadedAssetState);
        setError(
          resolvedAsset ? null : new Error('No playable animation clip was found.'),
        );
      })
      .catch((loadError) => {
        if (requestSeq !== requestSeqRef.current) return;

        loadedAssetStateRef.current = null;
        setLoadedAssetState(null);
        setError(
          loadError instanceof Error
            ? loadError
            : new Error('Failed to load GLTF motion preview asset.'),
        );
      })
      .finally(() => {
        if (requestSeq === requestSeqRef.current) {
          setIsLoading(false);
        }
      });
  }, [enabled, gltfUrl]);

  useEffect(
    () => () => {
      requestSeqRef.current += 1;
      const currentLoadedAssetState = loadedAssetStateRef.current;
      loadedAssetStateRef.current = null;
      disposeMotionPreviewGltfAsset(currentLoadedAssetState?.asset ?? null);
    },
    [],
  );

  return {
    asset,
    error,
    isLoading,
  };
}
